// ============================================================================
// Detailed RPG Health System - Types and Definitions
// ============================================================================
// Comprehensive health system with body parts, organs, conditions,
// mental health, sensory issues, temperature, and moods.
// ALL ENUMS ARE NUMERIC - NO MAGIC STRINGS
// ============================================================================
// ============================================================================
// Body Part Enums
// ============================================================================
export const BodyPartCategory = {
    VITAL: 0, // Cannot survive without (brain, heart)
    MAJOR: 1, // Severely impacts function (arms, legs, eyes)
    MINOR: 2, // Can live without (fingers, toes, ears)
    ORGAN: 3, // Internal organs
    SENSORY: 4, // Sensory organs
    SKELETAL: 5, // Bones and joints
};
export const BodyPartSide = {
    CENTER: 0,
    LEFT: 1,
    RIGHT: 2,
    BOTH: 3, // For paired organs that function together
};
// All possible body parts - NUMERIC ENUM
export const BodyPartType = {
    // Head region (0-19)
    HEAD: 0,
    BRAIN: 1,
    LEFT_EYE: 2,
    RIGHT_EYE: 3,
    LEFT_EAR: 4,
    RIGHT_EAR: 5,
    NOSE: 6,
    JAW: 7,
    TONGUE: 8,
    TEETH: 9,
    SKULL: 10,
    LEFT_OPTIC_NERVE: 11,
    RIGHT_OPTIC_NERVE: 12,
    LEFT_AUDITORY_NERVE: 13,
    RIGHT_AUDITORY_NERVE: 14,
    THROAT: 15,
    NECK: 16,
    // Torso region (20-49)
    TORSO: 20,
    HEART: 21,
    LEFT_LUNG: 22,
    RIGHT_LUNG: 23,
    LIVER: 24,
    STOMACH: 25,
    KIDNEY_LEFT: 26,
    KIDNEY_RIGHT: 27,
    SPINE: 28,
    RIBCAGE: 29,
    PELVIS: 30,
    INTESTINES: 31,
    APPENDIX: 32,
    SPLEEN: 33,
    PANCREAS: 34,
    GALLBLADDER: 35,
    BLADDER: 36,
    ADRENAL_LEFT: 37,
    ADRENAL_RIGHT: 38,
    THYROID: 39,
    DIAPHRAGM: 40,
    ESOPHAGUS: 41,
    // Arms (50-69)
    LEFT_SHOULDER: 50,
    LEFT_ARM: 51,
    LEFT_ELBOW: 52,
    LEFT_FOREARM: 53,
    LEFT_WRIST: 54,
    LEFT_HAND: 55,
    LEFT_THUMB: 56,
    LEFT_INDEX_FINGER: 57,
    LEFT_MIDDLE_FINGER: 58,
    LEFT_RING_FINGER: 59,
    LEFT_PINKY_FINGER: 60,
    RIGHT_SHOULDER: 61,
    RIGHT_ARM: 62,
    RIGHT_ELBOW: 63,
    RIGHT_FOREARM: 64,
    RIGHT_WRIST: 65,
    RIGHT_HAND: 66,
    RIGHT_THUMB: 67,
    RIGHT_INDEX_FINGER: 68,
    RIGHT_MIDDLE_FINGER: 69,
    RIGHT_RING_FINGER: 70,
    RIGHT_PINKY_FINGER: 71,
    // Legs (80-99)
    LEFT_HIP: 80,
    LEFT_LEG: 81,
    LEFT_KNEE: 82,
    LEFT_SHIN: 83,
    LEFT_ANKLE: 84,
    LEFT_FOOT: 85,
    LEFT_BIG_TOE: 86,
    RIGHT_HIP: 90,
    RIGHT_LEG: 91,
    RIGHT_KNEE: 92,
    RIGHT_SHIN: 93,
    RIGHT_ANKLE: 94,
    RIGHT_FOOT: 95,
    RIGHT_BIG_TOE: 96,
    // Blood and circulation (100-109)
    BLOOD: 100,
    BONE_MARROW: 101,
    LYMPH_NODES: 102,
    VEINS: 103,
    ARTERIES: 104,
    // Skin and exterior (110-119)
    SKIN: 110,
    HAIR: 111,
    NAILS: 112,
    // Nervous system (120-129)
    CENTRAL_NERVOUS_SYSTEM: 120,
    PERIPHERAL_NERVES: 121,
    VAGUS_NERVE: 122,
    SCIATIC_NERVE_LEFT: 123,
    SCIATIC_NERVE_RIGHT: 124,
    // Animal-specific (200-249)
    TAIL: 200,
    SNOUT: 201,
    BEAK: 202,
    WING_LEFT: 203,
    WING_RIGHT: 204,
    FIN_DORSAL: 205,
    FIN_TAIL: 206,
    GILLS: 207,
    HOOVES: 208,
    CLAWS: 209,
    FANGS: 210,
    HORNS: 211,
    SHELL: 212,
    ANTLERS: 213,
    WHISKERS: 214,
    TRUNK: 215,
    TUSKS: 216,
    VENOM_GLAND: 217,
    STINGER: 218,
    ANTENNA_LEFT: 219,
    ANTENNA_RIGHT: 220,
};
// ============================================================================
// Vital Functions - What body parts provide
// ============================================================================
export const VitalFunction = {
    CONSCIOUSNESS: 0, // Brain, central nervous system
    BLOOD_PUMPING: 1, // Heart
    BREATHING: 2, // Lungs, diaphragm
    FILTRATION: 3, // Kidneys, liver
    DIGESTION: 4, // Stomach, intestines
    MOVEMENT: 5, // Legs, spine, muscles
    MANIPULATION: 6, // Arms, hands, fingers
    SIGHT: 7, // Eyes, optic nerves
    HEARING: 8, // Ears, auditory nerves
    TALKING: 9, // Jaw, tongue, throat
    SMELL: 10, // Nose
    TASTE: 11, // Tongue
    BALANCE: 12, // Inner ear, cerebellum
    BLOOD_PRODUCTION: 13, // Bone marrow
    HORMONE_REGULATION: 14, // Thyroid, adrenals, pancreas
    IMMUNE_FUNCTION: 15, // Spleen, lymph nodes
    FINE_MOTOR: 16, // Fingers, hands
    PAIN_SENSATION: 17, // Peripheral nerves
    TEMPERATURE_REGULATION: 18, // Skin, hypothalamus
    REPRODUCTION: 19, // Reproductive organs
};
// ============================================================================
// Condition Severity and Categories
// ============================================================================
export const ConditionSeverity = {
    NEGLIGIBLE: 0, // Barely noticeable
    MINOR: 1, // Small debuff
    MODERATE: 2, // Noticeable impact
    SEVERE: 3, // Significant impact
    CRITICAL: 4, // Life-threatening
    EXTREME: 5, // Immediate death risk
    FATAL: 6, // Will die without immediate treatment
};
export const ConditionCategory = {
    INJURY: 0, // Physical damage
    DISEASE: 1, // Infections, illness
    CHRONIC: 2, // Long-term physical conditions
    MENTAL_ACUTE: 3, // Acute psychological states
    MENTAL_CHRONIC: 4, // Long-term mental health conditions
    NEUROLOGICAL: 5, // Brain and nerve disorders
    SENSORY: 6, // Vision, hearing, touch issues
    ENVIRONMENTAL: 7, // Temperature, exposure
    POISON: 8, // Toxins, venom
    ADDICTION: 9, // Substance dependencies
    AUTOIMMUNE: 10, // Body attacking itself
    GENETIC: 11, // Inherited conditions
    NUTRITIONAL: 12, // Deficiencies and excess
    CIRCULATORY: 13, // Blood and heart issues
    RESPIRATORY: 14, // Breathing issues
    ENDOCRINE: 15, // Hormone imbalances
    MUSCULOSKELETAL: 16, // Bones, joints, muscles
    DEVELOPMENTAL: 17, // Developmental conditions
};
// ============================================================================
// Condition Types - NUMERIC ENUM (Massively Expanded)
// ============================================================================
export const ConditionType = {
    // ===== INJURIES (0-99) =====
    // Cuts and lacerations
    CUT_MINOR: 0,
    CUT_DEEP: 1,
    LACERATION: 2,
    SLASH_WOUND: 3,
    // Blunt trauma
    BRUISE_MINOR: 10,
    BRUISE_MAJOR: 11,
    CONTUSION: 12,
    HEMATOMA: 13,
    INTERNAL_BLEEDING: 14,
    CRUSHED: 15,
    // Punctures
    PUNCTURE_MINOR: 20,
    PUNCTURE_DEEP: 21,
    STAB_WOUND: 22,
    IMPALED: 23,
    // Projectile
    GUNSHOT_GRAZE: 30,
    GUNSHOT_THROUGH: 31,
    GUNSHOT_LODGED: 32,
    SHRAPNEL: 33,
    ARROW_WOUND: 34,
    // Burns
    BURN_FIRST_DEGREE: 40,
    BURN_SECOND_DEGREE: 41,
    BURN_THIRD_DEGREE: 42,
    CHEMICAL_BURN: 43,
    ELECTRICAL_BURN: 44,
    RADIATION_BURN: 45,
    SUNBURN: 46,
    // Cold
    FROSTBITE_MINOR: 50,
    FROSTBITE_SEVERE: 51,
    FROSTBITE_GANGRENOUS: 52,
    // Animal attacks
    BITE_MINOR: 60,
    BITE_SEVERE: 61,
    BITE_VENOMOUS: 62,
    SCRATCH_MINOR: 63,
    SCRATCH_DEEP: 64,
    MAULED: 65,
    STING: 66,
    // Skeletal
    FRACTURE_HAIRLINE: 70,
    FRACTURE_SIMPLE: 71,
    FRACTURE_COMPOUND: 72,
    FRACTURE_COMMINUTED: 73, // Shattered
    DISLOCATION: 74,
    SPRAIN: 75,
    TORN_LIGAMENT: 76,
    TORN_TENDON: 77,
    BONE_BRUISE: 78,
    // Missing parts
    MISSING_PART: 80,
    AMPUTATED_CLEAN: 81,
    AMPUTATED_TRAUMATIC: 82,
    DESTROYED: 83,
    // Soft tissue
    TORN_MUSCLE: 85,
    MUSCLE_STRAIN: 86,
    HERNIA: 87,
    PROLAPSE: 88,
    // ===== DISEASES (100-199) =====
    // Bacterial
    INFECTION_MINOR: 100,
    INFECTION_MODERATE: 101,
    INFECTION_SEVERE: 102,
    SEPSIS: 103,
    SEPTIC_SHOCK: 104,
    GANGRENE: 105,
    TETANUS: 106,
    PLAGUE: 107,
    TUBERCULOSIS: 108,
    CHOLERA: 109,
    TYPHOID: 110,
    LYME_DISEASE: 111,
    PNEUMONIA_BACTERIAL: 112,
    MENINGITIS_BACTERIAL: 113,
    // Viral
    FLU: 120,
    COMMON_COLD: 121,
    PNEUMONIA_VIRAL: 122,
    RABIES: 123,
    HEPATITIS: 124,
    MENINGITIS_VIRAL: 125,
    ENCEPHALITIS: 126,
    // Parasitic
    PARASITIC_WORMS: 130,
    MALARIA: 131,
    PARASITIC_SKIN: 132,
    // Fungal
    FUNGAL_SKIN: 135,
    FUNGAL_SYSTEMIC: 136,
    // Food/water
    FOOD_POISONING_MILD: 140,
    FOOD_POISONING_SEVERE: 141,
    DYSENTERY: 142,
    // Blood
    BLOOD_LOSS_MINOR: 150,
    BLOOD_LOSS_MODERATE: 151,
    BLOOD_LOSS_SEVERE: 152,
    BLOOD_LOSS_CRITICAL: 153,
    ANEMIA: 154,
    HEMOPHILIA: 155,
    BLOOD_CLOT: 156,
    DEEP_VEIN_THROMBOSIS: 157,
    PULMONARY_EMBOLISM: 158,
    STROKE: 159,
    HEART_ATTACK: 160,
    // Cancer
    TUMOR_BENIGN: 165,
    TUMOR_MALIGNANT: 166,
    CANCER_EARLY: 167,
    CANCER_ADVANCED: 168,
    CANCER_TERMINAL: 169,
    // Organ failure
    KIDNEY_FAILURE_ACUTE: 175,
    KIDNEY_FAILURE_CHRONIC: 176,
    LIVER_FAILURE_ACUTE: 177,
    LIVER_FAILURE_CHRONIC: 178,
    HEART_FAILURE: 179,
    RESPIRATORY_FAILURE: 180,
    MULTI_ORGAN_FAILURE: 181,
    // ===== CHRONIC PHYSICAL (200-299) =====
    ASTHMA: 200,
    COPD: 201,
    EMPHYSEMA: 202,
    CHRONIC_BRONCHITIS: 203,
    BAD_BACK: 210,
    CHRONIC_PAIN: 211,
    FIBROMYALGIA: 212,
    ARTHRITIS: 213,
    RHEUMATOID_ARTHRITIS: 214,
    OSTEOPOROSIS: 215,
    SCOLIOSIS: 216,
    DEGENERATIVE_DISC: 217,
    DIABETES_TYPE_1: 220,
    DIABETES_TYPE_2: 221,
    HYPOGLYCEMIA: 222,
    HYPERGLYCEMIA: 223,
    HYPERTENSION: 230,
    HYPOTENSION: 231,
    ARRHYTHMIA: 232,
    HEART_MURMUR: 233,
    CHRONIC_FATIGUE: 240,
    SLEEP_APNEA: 241,
    NARCOLEPSY: 242,
    IRRITABLE_BOWEL: 250,
    CROHNS_DISEASE: 251,
    ULCERATIVE_COLITIS: 252,
    ACID_REFLUX: 253,
    ULCER_STOMACH: 254,
    KIDNEY_STONES: 260,
    GALLSTONES: 261,
    SCAR_MINOR: 270,
    SCAR_DISFIGURING: 271,
    KELOID: 272,
    FRAIL: 275,
    ELDERLY: 276,
    // ===== MENTAL - ACUTE (300-349) =====
    STRESSED: 300,
    ANXIOUS: 301,
    PANIC_ATTACK: 302,
    DEPRESSIVE_EPISODE: 303,
    MANIC_EPISODE: 304,
    PSYCHOTIC_BREAK: 305,
    DISSOCIATIVE_EPISODE: 306,
    BERSERK: 307,
    CATATONIC: 308,
    FUGUE_STATE: 309,
    SUICIDAL_IDEATION: 310,
    HOMICIDAL_RAGE: 311,
    OVERWHELMED: 312,
    GRIEF_ACUTE: 313,
    FLASHBACK: 314,
    DEREALIZATION: 315,
    DEPERSONALIZATION: 316,
    // ===== MENTAL - CHRONIC (350-449) =====
    // Anxiety disorders
    GENERALIZED_ANXIETY: 350,
    SOCIAL_ANXIETY: 351,
    PANIC_DISORDER: 352,
    AGORAPHOBIA: 353,
    CLAUSTROPHOBIA: 354,
    SPECIFIC_PHOBIA: 355,
    HEALTH_ANXIETY: 356,
    SEPARATION_ANXIETY: 357,
    // Trauma and stress
    PTSD: 360,
    COMPLEX_PTSD: 361,
    ACUTE_STRESS_DISORDER: 362,
    ADJUSTMENT_DISORDER: 363,
    // Mood disorders
    MAJOR_DEPRESSION: 370,
    PERSISTENT_DEPRESSION: 371,
    BIPOLAR_TYPE_1: 372,
    BIPOLAR_TYPE_2: 373,
    CYCLOTHYMIA: 374,
    SEASONAL_AFFECTIVE: 375,
    // Obsessive-compulsive spectrum
    OCD: 380,
    BODY_DYSMORPHIA: 381,
    HOARDING_DISORDER: 382,
    TRICHOTILLOMANIA: 383, // Hair pulling
    EXCORIATION: 384, // Skin picking
    // Personality disorders
    BORDERLINE_PD: 390,
    ANTISOCIAL_PD: 391,
    NARCISSISTIC_PD: 392,
    AVOIDANT_PD: 393,
    DEPENDENT_PD: 394,
    PARANOID_PD: 395,
    SCHIZOID_PD: 396,
    SCHIZOTYPAL_PD: 397,
    HISTRIONIC_PD: 398,
    OBSESSIVE_COMPULSIVE_PD: 399,
    // Psychotic disorders
    SCHIZOPHRENIA: 400,
    SCHIZOAFFECTIVE: 401,
    DELUSIONAL_DISORDER: 402,
    BRIEF_PSYCHOTIC: 403,
    // Dissociative disorders
    DISSOCIATIVE_IDENTITY: 410,
    DISSOCIATIVE_AMNESIA: 411,
    DEPERSONALIZATION_DISORDER: 412,
    // Eating disorders
    ANOREXIA: 420,
    BULIMIA: 421,
    BINGE_EATING: 422,
    AVOIDANT_FOOD_INTAKE: 423,
    // Neurodevelopmental
    AUTISM_SPECTRUM: 430,
    ADHD: 431,
    TOURETTES: 432,
    INTELLECTUAL_DISABILITY: 433,
    LEARNING_DISABILITY: 434,
    // Other mental
    GRIEF_PROLONGED: 440,
    ATTACHMENT_DISORDER: 441,
    CONDUCT_DISORDER: 442,
    INTERMITTENT_EXPLOSIVE: 443,
    // ===== NEUROLOGICAL (450-499) =====
    DEMENTIA_EARLY: 450,
    DEMENTIA_MODERATE: 451,
    DEMENTIA_SEVERE: 452,
    ALZHEIMERS: 453,
    PARKINSONS: 454,
    HUNTINGTONS: 455,
    MULTIPLE_SCLEROSIS: 456,
    ALS: 457,
    EPILEPSY: 460,
    SEIZURE: 461,
    STATUS_EPILEPTICUS: 462,
    MIGRAINE: 465,
    CLUSTER_HEADACHE: 466,
    TENSION_HEADACHE: 467,
    CHRONIC_HEADACHE: 468,
    VERTIGO: 470,
    MENIERES_DISEASE: 471,
    NEUROPATHY_PERIPHERAL: 472,
    NEUROPATHY_DIABETIC: 473,
    SCIATICA: 474,
    CARPAL_TUNNEL: 475,
    NERVE_DAMAGE: 476,
    RADICULOPATHY: 477,
    TREMOR_ESSENTIAL: 480,
    TREMOR_PARKINSONIAN: 481,
    DYSTONIA: 482,
    ATAXIA: 483,
    CONCUSSION: 485,
    TRAUMATIC_BRAIN_INJURY: 486,
    POST_CONCUSSION_SYNDROME: 487,
    BRAIN_TUMOR: 490,
    BRAIN_ANEURYSM: 491,
    HYDROCEPHALUS: 492,
    // ===== SENSORY (500-549) =====
    // Vision
    BLINDNESS_PARTIAL: 500,
    BLINDNESS_COMPLETE: 501,
    CATARACTS: 502,
    GLAUCOMA: 503,
    MACULAR_DEGENERATION: 504,
    RETINAL_DETACHMENT: 505,
    PHOTOPHOBIA: 506, // Light sensitivity
    NYCTALOPIA: 507, // Night blindness
    COLOR_BLINDNESS: 508,
    DOUBLE_VISION: 509,
    FLOATERS: 510,
    DRY_EYES: 511,
    EYE_STRAIN: 512,
    // Hearing
    DEAFNESS_PARTIAL: 520,
    DEAFNESS_COMPLETE: 521,
    TINNITUS: 522,
    HYPERACUSIS: 523, // Sound sensitivity
    MISOPHONIA: 524, // Hatred of specific sounds
    AUDITORY_PROCESSING: 525,
    // Touch
    NUMBNESS_PARTIAL: 530,
    NUMBNESS_COMPLETE: 531,
    PHANTOM_PAIN: 532,
    ALLODYNIA: 533, // Pain from non-painful stimuli
    HYPERESTHESIA: 534, // Increased sensitivity
    HYPOESTHESIA: 535, // Decreased sensitivity
    TACTILE_DEFENSIVENESS: 536,
    // Smell/Taste
    ANOSMIA: 540, // No smell
    PAROSMIA: 541, // Distorted smell
    AGEUSIA: 542, // No taste
    DYSGEUSIA: 543, // Distorted taste
    // ===== ENVIRONMENTAL (550-599) =====
    HYPOTHERMIA_MILD: 550,
    HYPOTHERMIA_MODERATE: 551,
    HYPOTHERMIA_SEVERE: 552,
    HYPERTHERMIA_MILD: 555,
    HYPERTHERMIA_MODERATE: 556,
    HYPERTHERMIA_SEVERE: 557,
    HEAT_STROKE: 558,
    EXHAUSTED: 560,
    FATIGUED: 561,
    SLEEP_DEPRIVED: 562,
    STARVING_MILD: 565,
    STARVING_MODERATE: 566,
    STARVING_SEVERE: 567,
    DEHYDRATED_MILD: 570,
    DEHYDRATED_MODERATE: 571,
    DEHYDRATED_SEVERE: 572,
    ALTITUDE_SICKNESS: 575,
    DECOMPRESSION_SICKNESS: 576,
    DROWNING_RECOVERY: 577,
    SMOKE_INHALATION: 578,
    OXYGEN_DEPRIVATION: 579,
    RADIATION_MILD: 580,
    RADIATION_MODERATE: 581,
    RADIATION_SEVERE: 582,
    RADIATION_SICKNESS: 583,
    ELECTROCUTED: 585,
    LIGHTNING_STRUCK: 586,
    // ===== POISON AND TOXINS (600-649) =====
    POISONED_MILD: 600,
    POISONED_MODERATE: 601,
    POISONED_SEVERE: 602,
    VENOMED_NEUROTOXIC: 610, // Affects nerves
    VENOMED_HEMOTOXIC: 611, // Affects blood
    VENOMED_CYTOTOXIC: 612, // Destroys tissue
    VENOMED_CARDIOTOXIC: 613, // Affects heart
    PARALYSIS_PARTIAL: 620,
    PARALYSIS_COMPLETE: 621,
    RESPIRATORY_PARALYSIS: 622,
    ALCOHOL_INTOXICATION: 625,
    DRUG_INTOXICATION: 626,
    OVERDOSE: 627,
    ALLERGIC_MILD: 630,
    ALLERGIC_SEVERE: 631,
    ANAPHYLAXIS: 632,
    HEAVY_METAL_POISONING: 635,
    CARBON_MONOXIDE: 636,
    // ===== ADDICTION (650-699) =====
    ADDICTION_ALCOHOL: 650,
    ADDICTION_OPIOID: 651,
    ADDICTION_STIMULANT: 652,
    ADDICTION_SEDATIVE: 653,
    ADDICTION_NICOTINE: 654,
    ADDICTION_GAMBLING: 655,
    ADDICTION_GENERAL: 656,
    WITHDRAWAL_MILD: 660,
    WITHDRAWAL_MODERATE: 661,
    WITHDRAWAL_SEVERE: 662,
    WITHDRAWAL_DANGEROUS: 663, // DTs, seizure risk
    TOLERANCE_BUILT: 670,
    CRAVING: 671,
    // ===== AUTOIMMUNE (700-749) =====
    LUPUS: 700,
    HASHIMOTOS: 701,
    GRAVES_DISEASE: 702,
    CELIAC: 703,
    PSORIASIS: 704,
    VITILIGO: 705,
    ALOPECIA_AREATA: 706,
    // ===== NUTRITIONAL (750-799) =====
    VITAMIN_DEFICIENCY_A: 750,
    VITAMIN_DEFICIENCY_B: 751,
    VITAMIN_DEFICIENCY_C: 752,
    VITAMIN_DEFICIENCY_D: 753,
    IRON_DEFICIENCY: 754,
    CALCIUM_DEFICIENCY: 755,
    MALNUTRITION: 756,
    OBESITY: 757,
    MORBID_OBESITY: 758,
    SCURVY: 760,
    RICKETS: 761,
    BERIBERI: 762,
    PELLAGRA: 763,
    // ===== ENDOCRINE (800-849) =====
    HYPOTHYROIDISM: 800,
    HYPERTHYROIDISM: 801,
    CUSHINGS_SYNDROME: 802,
    ADDISONS_DISEASE: 803,
    PITUITARY_DISORDER: 804,
    GROWTH_HORMONE_DEFICIENCY: 805,
    PCOS: 806,
    // ===== RESPIRATORY (850-899) =====
    BRONCHITIS_ACUTE: 850,
    COUGH_CHRONIC: 851,
    PLEURISY: 852,
    PNEUMOTHORAX: 853,
    PULMONARY_FIBROSIS: 854,
    SILICOSIS: 855,
    ASBESTOSIS: 856,
    // ===== SPECIAL STATES (900-999) =====
    UNCONSCIOUS: 900,
    COMA: 901,
    BRAIN_DEAD: 902,
    DEAD: 903,
    SEDATED: 910,
    ANESTHETIZED: 911,
    INTUBATED: 912,
    PREGNANT: 920,
    POSTPARTUM: 921,
    RECOVERING: 930,
    CONVALESCING: 931,
    // Prosthetics and implants (positive conditions)
    PROSTHETIC_BASIC: 940,
    PROSTHETIC_ADVANCED: 941,
    BIONIC_IMPLANT: 942,
    ARTIFICIAL_ORGAN: 943,
    PACEMAKER: 944,
    COCHLEAR_IMPLANT: 945,
};
// ============================================================================
// Temperature
// ============================================================================
export const TemperatureComfort = {
    LETHAL_COLD: 0,
    FREEZING: 1,
    COLD: 2,
    CHILLY: 3,
    COMFORTABLE: 4,
    WARM: 5,
    HOT: 6,
    SCORCHING: 7,
    LETHAL_HOT: 8,
};
export const TEMPERATURE_THRESHOLDS = {
    LETHAL_COLD: -40,
    FREEZING: -20,
    COLD: 0,
    CHILLY: 10,
    COMFORTABLE_MIN: 18,
    COMFORTABLE_MAX: 26,
    WARM: 30,
    HOT: 38,
    SCORCHING: 45,
    LETHAL_HOT: 55,
};
// ============================================================================
// Mood and Mental State
// ============================================================================
export const MoodCategory = {
    ECSTATIC: 0,
    HAPPY: 1,
    CONTENT: 2,
    NEUTRAL: 3,
    UNHAPPY: 4,
    STRESSED: 5,
    BREAKING: 6, // About to have a mental break
    BROKEN: 7, // In mental break state
};
export const ThoughtCategory = {
    // Environment
    ENVIRONMENT_POSITIVE: 0,
    ENVIRONMENT_NEGATIVE: 1,
    // Physical needs
    NEED_SATISFIED: 10,
    NEED_UNSATISFIED: 11,
    // Social
    SOCIAL_POSITIVE: 20,
    SOCIAL_NEGATIVE: 21,
    // Work
    WORK_POSITIVE: 30,
    WORK_NEGATIVE: 31,
    // Health
    HEALTH_POSITIVE: 40,
    HEALTH_NEGATIVE: 41,
    // Trauma
    TRAUMA: 50,
    // Personality
    PERSONALITY_TRAIT: 60,
    // Events
    EVENT_POSITIVE: 70,
    EVENT_NEGATIVE: 71,
    // Relationships
    RELATIONSHIP_POSITIVE: 80,
    RELATIONSHIP_NEGATIVE: 81,
    // Existential
    EXISTENTIAL: 90,
};
export const ThoughtType = {
    // ===== ENVIRONMENT (0-49) =====
    BEAUTIFUL_ROOM: 0,
    SPACIOUS_ROOM: 1,
    IMPRESSIVE_DINING: 2,
    COMFORTABLE_BED: 3,
    OWN_ROOM: 4,
    NATURE_BEAUTY: 5,
    UGLY_ROOM: 10,
    CRAMPED_ROOM: 11,
    ATE_STANDING: 12,
    SLEPT_GROUND: 13,
    SLEPT_OUTSIDE: 14,
    DISTURBED_SLEEP: 15,
    DARKNESS: 16,
    FILTHY_ENVIRONMENT: 17,
    SAW_CORPSE: 18,
    SAW_ROTTING_CORPSE: 19,
    WITNESSED_BUTCHERING: 20,
    SMELLED_DECAY: 21,
    HEARD_VIOLENCE: 22,
    UNCOMFORTABLE_TEMPERATURE: 23,
    // ===== PHYSICAL NEEDS (50-99) =====
    ATE_FINE_MEAL: 50,
    ATE_LAVISH_MEAL: 51,
    ATE_FAVORITE_FOOD: 52,
    WELL_RESTED: 53,
    COMFORTABLE_TEMPERATURE_THOUGHT: 54,
    QUENCHED_THIRST: 55,
    ATE_RAW_FOOD: 60,
    ATE_AWFUL_MEAL: 61,
    ATE_CORPSE: 62,
    ATE_SAME_FOOD_REPEATEDLY: 63,
    HUNGRY: 64,
    VERY_HUNGRY: 65,
    STARVING_THOUGHT: 66,
    TIRED: 67,
    EXHAUSTED_THOUGHT: 68,
    SLEEP_DEPRIVED_THOUGHT: 69,
    THIRSTY: 70,
    VERY_THIRSTY: 71,
    IN_PAIN: 72,
    IN_EXTREME_PAIN: 73,
    // ===== SOCIAL (100-149) =====
    HAD_NICE_CHAT: 100,
    MADE_FRIEND: 101,
    ROMANCED: 102,
    GOT_MARRIED: 103,
    HAD_PARTY: 104,
    RECEIVED_GIFT: 105,
    HELPED_SOMEONE: 106,
    WAS_HELPED: 107,
    COMPLIMENTED: 108,
    INSULTED: 110,
    REJECTED: 111,
    IGNORED: 112,
    BETRAYED: 113,
    CHEATED_ON: 114,
    DIVORCED: 115,
    ARGUED: 116,
    WITNESSED_ARGUMENT: 117,
    SOCIAL_ISOLATION: 118,
    OVERCROWDED: 119,
    // ===== WORK (150-199) =====
    COMPLETED_PROJECT: 150,
    SKILL_IMPROVED: 151,
    CREATIVE_WORK: 152,
    HELPED_COLONY: 153,
    RESCUED_SOMEONE: 154,
    BORING_WORK: 160,
    FAILED_TASK: 161,
    OVERWORKED: 162,
    UNDERWORKED: 163,
    FORCED_LABOR: 164,
    // ===== HEALTH (200-249) =====
    HEALED: 200,
    SURGERY_SUCCESS: 201,
    GOT_PROSTHETIC: 202,
    RECOVERED_FROM_ILLNESS: 203,
    INJURED_THOUGHT: 210,
    SICK_THOUGHT: 211,
    CHRONIC_PAIN_THOUGHT: 212,
    SCARRED: 213,
    DISFIGURED: 214,
    LOST_LIMB: 215,
    ORGAN_HARVESTED: 216,
    // ===== TRAUMA (250-299) =====
    WITNESSED_DEATH: 250,
    WITNESSED_MURDER: 251,
    WITNESSED_FRIENDLY_FIRE: 252,
    KILLED_SOMEONE: 253,
    KILLED_INNOCENT: 254,
    TORTURED_SOMEONE: 255,
    WAS_TORTURED: 256,
    NEAR_DEATH_EXPERIENCE: 257,
    SURVIVED_ATTACK: 258,
    LOST_LOVED_ONE: 259,
    LOST_PET: 260,
    HOME_DESTROYED: 261,
    CAPTURED: 262,
    ENSLAVED: 263,
    ESCAPED_CAPTIVITY: 264,
    PTSD_FLASHBACK: 265,
    NIGHTMARE_THOUGHT: 266,
    // ===== RELATIONSHIPS (300-349) =====
    LOVED_ONE_NEARBY: 300,
    FAMILY_REUNITED: 301,
    CHILD_BORN: 302,
    PET_BONDING: 303,
    LOVED_ONE_FAR: 310,
    FAMILY_SEPARATED: 311,
    RELATIONSHIP_ENDED: 312,
    RIVAL_NEARBY: 313,
    // ===== PERSONALITY/TRAITS (350-399) =====
    ENJOYED_SOLITUDE: 350,
    ENJOYED_SOCIALIZING: 351,
    OUTDOOR_LOVER: 352,
    INDOOR_COMFORT: 353,
    CREATIVE_EXPRESSION: 354,
    INTELLECTUAL_PURSUIT: 355,
    PHYSICAL_ACTIVITY: 356,
    NEEDS_SOLITUDE: 360,
    NEEDS_PEOPLE: 361,
    CABIN_FEVER: 362,
    MISSES_OUTDOORS: 363,
    BORED: 364,
    UNDERSTIMULATED: 365,
    OVERSTIMULATED: 366,
    // ===== EVENTS (400-449) =====
    CELEBRATION: 400,
    VICTORY: 401,
    GOOD_NEWS: 402,
    DISCOVERED_SOMETHING: 403,
    DISASTER: 410,
    DEFEAT: 411,
    BAD_NEWS: 412,
    BETRAYAL: 413,
    // ===== EXISTENTIAL (450-499) =====
    SENSE_OF_PURPOSE: 450,
    HOPE: 451,
    ACCOMPLISHMENT: 452,
    SPIRITUAL_PEACE: 453,
    EXISTENTIAL_DREAD: 460,
    HOPELESSNESS: 461,
    MEANINGLESSNESS: 462,
    GUILT: 463,
    SHAME: 464,
    REGRET: 465,
};
// ============================================================================
// Body Template
// ============================================================================
export const BodyTemplate = {
    HUMANOID: 0,
    QUADRUPED_LARGE: 1, // Bear, horse
    QUADRUPED_MEDIUM: 2, // Wolf, deer
    QUADRUPED_SMALL: 3, // Rabbit, cat
    BIRD_LARGE: 4, // Eagle
    BIRD_MEDIUM: 5, // Chicken
    BIRD_SMALL: 6, // Sparrow
    FISH: 7,
    INSECT_SMALL: 8,
    INSECT_LARGE: 9,
    SERPENT: 10,
    CRUSTACEAN: 11,
    ARACHNID: 12,
    AMPHIBIAN: 13,
    WORM: 14,
};
// ============================================================================
// Mental Break Types
// ============================================================================
export const MentalBreakType = {
    NONE: 0,
    // Minor breaks
    FOOD_BINGE: 10,
    HIDE_IN_ROOM: 11,
    WANDER_SAD: 12,
    CRYING_FIT: 13,
    TANTRUM: 14,
    INSULTING_SPREE: 15,
    // Major breaks
    BERSERK: 20,
    MURDEROUS_RAGE: 21,
    FIRE_STARTING: 22,
    DESTRUCTION_SPREE: 23,
    CATATONIC: 24,
    PSYCHOTIC_WANDERING: 25,
    // Extreme breaks
    GIVE_UP: 30, // Collapse and refuse to move
    SUICIDE_ATTEMPT: 31,
    MASS_VIOLENCE: 32,
};
// ============================================================================
// Combat and Damage
// ============================================================================
export const DamageType = {
    BLUNT: 0,
    SHARP: 1,
    STAB: 2,
    BULLET: 3,
    BURN: 4,
    COLD: 5,
    BITE: 6,
    SCRATCH: 7,
    TOXIC: 8,
    ELECTRIC: 9,
    RADIATION: 10,
    CRUSH: 11,
    EXPLOSIVE: 12,
    PSYCHIC: 13,
    DISEASE: 14,
};
//# sourceMappingURL=types.js.map